// The closed catalog of things an offer summary can say.
//
// CONSUMER: shared (Workers extraction route + web review panel + tests).
//
// WHY A CLOSED LIST. Nate dictates an offer and the OS proposes values
// for the blanks on the AAR forms. An open extractor — "pull out
// whatever you find" — produces `sellerRentBack` on Tuesday and
// `seller_rent_back_days` on Wednesday, and nothing downstream can bind
// to either. Worse, it has no defined behaviour to test. A closed list
// means "what can this feature put on a contract?" is a file a person
// can read, and every entry has a named test.
//
// WHY EACH SLOT NAMES A MERGE TOKEN. The model never chooses which
// blank on which form gets the price — that would be the dangerous
// version of this feature. A slot reaches a specific blank through the
// merge-token binding a HUMAN authored when they placed the field, which
// the saved layout then remembers per form. This column is that join,
// and it is the only one.
//
// Pure and dependency-free — same rule as field-types.ts.

/** How a slot's value is written down, which decides how it is parsed
 *  and, more importantly, when it must be treated as ambiguous. */
export type OfferSlotType =
  | "money"
  | "date"
  | "integer_days"
  | "boolean"
  | "enum"
  | "text"
  /** Deliberately dual — "ten down" is $10,000 or 10% and both are
   *  ordinary things to say out loud. Collapsing them at parse time is
   *  exactly the invisible-wrong-number failure this design exists to
   *  stop, so these are ALWAYS ambiguous unless a unit was spoken. */
  | "money_or_percent"
  // ── Typed shapes added 2026-11-04 (offer-writing program, session 1) ──
  // Each of these names the canonical value shared/realty/
  // offer-term-values.ts reads out of the words, so a form's boxes can
  // be filled from PARTS (month / day / year; ordered-by / paid-by /
  // cap) rather than from one string nothing can compute from.
  /** A calendar date, or a number of days counted from acceptance
   *  ("close 21 days after acceptance"). Both are real answers to
   *  "when do we close", and the second one has no date until the
   *  seller signs. */
  | "date_or_duration"
  /** A date WITH a time of day, in Arizona time ("November 6 at 5pm"). */
  | "datetime"
  /** Ordered by, paid by, cost cap, plan, company, or declined. */
  | "home_warranty"
  /** At close of escrow, or alternate terms. */
  | "possession"
  /** Named people — a second buyer — with an email when one was said. */
  | "people"
  /** Contingent on selling the buyer's own home: the address, whether
   *  it is under contract, its closing date, the deadline. */
  | "sale_of_home_contingency"
  /** An escalation clause: start, step, cap. */
  | "escalation"
  /** A seller lease-back after close: days and rent. */
  | "lease_back";

export interface OfferSlot {
  key: string;
  /** What a person calls it. Never the key. */
  label: string;
  type: OfferSlotType;
  /** The merge token that carries this value onto a form. A field bound
   *  to this token prints the accepted value at send time. */
  token: string;
  /** Allowed values, for `enum` slots only. */
  options?: readonly string[];
  /** Shown in the review panel and given to the model as an example of
   *  the SHAPE of an answer, never as a default. */
  example: string;
  /** Told to the model so it knows what this blank means on an AAR
   *  form. Kept here rather than in the prompt so the catalog is the one
   *  place a slot is defined. */
  hint: string;
}

export const LOAN_TYPES = ["Conventional", "FHA", "VA", "USDA", "Cash", "Other"] as const;

/** Who pays an HOA fee, in the HOA addendum's own three boxes. */
export const HOA_FEE_PAYERS = ["Buyer", "Seller", "Other"] as const;

/** How the earnest money arrives, in the purchase contract's own boxes
 *  (line 11: personal check, wire transfer, other). */
export const EARNEST_MONEY_FORMS = ["Personal check", "Wire transfer", "Other"] as const;

/** Who holds the earnest money (line 11: the escrow company, or the
 *  broker's trust account). */
export const EARNEST_MONEY_HOLDERS = ["Escrow company", "Broker's trust account"] as const;

/**
 * An enum answer matched the way a person says it, not the way the
 * catalog spells it (2026-11-04). "buyer pays the HOA transfer fee"
 * produced "buyer", the catalog said "Buyer", and the proposal was
 * dropped for not being an option. Returns the catalog's own spelling,
 * or null when the words name no option at all. Prefix matches are
 * refused: "Other" must be said as other, never inferred from "o".
 */
const ENUM_GENERIC_WORDS: ReadonlySet<string> = new Set(["company", "account", "transfer", "other", "s"]);

export function matchEnumOption(slot: OfferSlot, value: string): string | null {
  if (!slot.options) return null;
  const wanted = value.trim().toLowerCase().replace(/[.\s]+$/g, "");
  if (!wanted) return null;
  for (const option of slot.options) {
    if (option.toLowerCase() === wanted) return option;
  }
  // The spoken forms: "deposited with escrow" for "Escrow company",
  // "wire" for "Wire transfer", "conventional loan" for "Conventional",
  // "cash offer" for "Cash". Each option's distinctive words are looked
  // for as whole words in what was said; the generic ones ("company",
  // "account", "transfer", "other") name nothing on their own. Exactly
  // ONE option may match this way — two is a sentence naming both, and
  // that is refused rather than picked from.
  const spokenWords = new Set(wanted.split(/[^a-z]+/).filter(Boolean));
  const hits: string[] = [];
  for (const option of slot.options) {
    const keywords = option
      .toLowerCase()
      .split(/[\s']+/)
      .filter((w) => w.length >= 2 && !ENUM_GENERIC_WORDS.has(w));
    if (option === "Broker's trust account") keywords.push("trust");
    if (keywords.some((w) => spokenWords.has(w))) hits.push(option);
  }
  return hits.length === 1 ? (hits[0] ?? null) : null;
}

/**
 * v1 slots. Ordered the way an agent talks through an offer, which is
 * also the order the review panel lists them — price first, because it
 * is both the most important number and the one most often said
 * ambiguously.
 */
export const OFFER_SLOTS: readonly OfferSlot[] = [
  {
    key: "purchase_price",
    label: "Purchase price",
    type: "money",
    token: "offer.purchase_price",
    example: "$350,000",
    hint: "What the buyer is offering for the property.",
  },
  {
    key: "earnest_money",
    label: "Earnest money",
    type: "money",
    token: "offer.earnest_money",
    example: "$5,000",
    hint: "The deposit that goes to escrow with the offer.",
  },
  {
    key: "earnest_money_form",
    label: "Earnest money: how it's paid",
    type: "enum",
    token: "offer.earnest_money_form",
    options: EARNEST_MONEY_FORMS,
    example: "Wire transfer",
    hint:
      "How the earnest money arrives: a personal check, a wire, or something else. Only when " +
      "the advisor said how.",
  },
  {
    key: "earnest_money_held_by",
    label: "Earnest money: held by",
    type: "enum",
    token: "offer.earnest_money_held_by",
    options: EARNEST_MONEY_HOLDERS,
    example: "Escrow company",
    hint:
      "Who holds the deposit: the escrow (title) company, or the broker's trust account. " +
      "'deposited with escrow' means the escrow company.",
  },
  {
    key: "close_of_escrow_date",
    label: "Close of escrow",
    type: "date_or_duration",
    token: "offer.close_of_escrow_date",
    example: "October 15, 2026 — or: 21 days after acceptance",
    hint:
      "The closing date. A calendar date is written as a date. A number of days is written as " +
      "'N days after acceptance' and LEFT as days — closing windows run from contract " +
      "acceptance, which has not happened, so never turn days into a date.",
  },
  {
    key: "possession",
    label: "Possession",
    type: "possession",
    token: "offer.possession",
    example: "At close of escrow — or: 3 days after close of escrow",
    hint:
      "When the buyer gets the keys. 'At close of escrow' is the contract's own default box; " +
      "anything else is the alternate terms line.",
  },
  {
    key: "loan_type",
    label: "Loan type",
    type: "enum",
    token: "offer.loan_type",
    options: LOAN_TYPES,
    example: "Conventional",
    hint: "How the purchase is financed.",
  },
  {
    key: "loan_amount",
    label: "Loan amount",
    type: "money",
    token: "offer.loan_amount",
    example: "$280,000",
    hint: "The amount being borrowed.",
  },
  {
    key: "down_payment",
    label: "Down payment",
    type: "money_or_percent",
    token: "offer.down_payment",
    example: "20% or $70,000",
    hint:
      "How much the buyer is putting down. ALWAYS ambiguous unless the summary says a unit — " +
      "'ten down' could be $10,000 or 10%.",
  },
  {
    key: "seller_concessions",
    label: "Seller concessions",
    type: "money_or_percent",
    token: "offer.seller_concessions",
    example: "$3,500 or 2%",
    hint:
      "What the seller is being asked to contribute toward closing costs: a dollar amount OR a " +
      "percentage of the price (the contract has a box for each). ALWAYS ambiguous unless the " +
      "summary says % or $.",
  },
  {
    key: "repair_credit",
    label: "Repair credit",
    type: "money",
    token: "offer.repair_credit",
    example: "$1,200",
    hint: "A credit in place of repairs.",
  },
  {
    key: "inspection_period_days",
    label: "Inspection period",
    type: "integer_days",
    token: "offer.inspection_period_days",
    example: "10 days",
    hint:
      "The buyer's due-diligence window, in days. 'The standard period' is an INTERPRETATION — " +
      "this brokerage's standard is not something you can know.",
  },
  {
    key: "appraisal_contingency",
    label: "Appraisal contingency",
    type: "boolean",
    token: "offer.appraisal_contingency",
    example: "Yes",
    hint: "Whether the offer is contingent on the appraisal.",
  },
  {
    key: "financing_contingency",
    label: "Financing contingency",
    type: "boolean",
    token: "offer.financing_contingency",
    example: "Yes",
    hint: "Whether the offer is contingent on the buyer's loan.",
  },
  {
    key: "home_warranty",
    label: "Home warranty",
    type: "home_warranty",
    token: "offer.home_warranty",
    example: "Ordered by seller, paid by seller, up to $600",
    hint:
      "Home warranty terms, if the offer asks for one: who orders it, who pays, the cost cap, " +
      "and the plan or company when named. 'Declined' when the buyer waives it. A plain " +
      "'$600 seller warranty' means the seller orders and pays.",
  },
  {
    key: "personal_property_included",
    label: "Personal property included",
    type: "text",
    token: "offer.personal_property_included",
    example: "Refrigerator, washer and dryer",
    hint: "Items conveying with the house that would not otherwise.",
  },
  // ── Terms the catalog could not hold before 2026-11-04 ─────────────
  // Each of these came back from the reader as "nothing in the offer
  // forms covers this — you'll need to write that in" on the audit's
  // three offers, and every one has a box on an AAR form.
  {
    key: "additional_buyers",
    label: "Additional buyers",
    type: "people",
    token: "offer.additional_buyers",
    example: "Jordan Example (jordan@example.com)",
    hint:
      "A second (or third) buyer named on the offer beyond the buyer whose record this is: " +
      "their name, and their email when given. One per entry, separated by semicolons.",
  },
  {
    key: "buyer_vesting",
    label: "Buyer vesting",
    type: "text",
    token: "offer.buyer_vesting",
    example: "Community property with right of survivorship",
    hint: "How the buyer(s) will take title, when the advisor said so.",
  },
  {
    key: "sale_of_home_contingency",
    label: "Sale of buyer's home contingency",
    type: "sale_of_home_contingency",
    token: "offer.sale_of_home_contingency",
    example: "Contingent on the sale of 99 Example Way; under contract; closing December 1, 2026",
    hint:
      "Whether the offer depends on the buyer selling their own home: the address of that " +
      "home, whether it is already under contract, its closing date, and any deadline to be " +
      "under contract by.",
  },
  {
    key: "appraisal_gap",
    label: "Appraisal gap coverage",
    type: "money",
    token: "offer.appraisal_gap",
    example: "$10,000",
    hint: "How much the buyer will pay above the appraised value, if the house appraises low.",
  },
  {
    key: "escalation",
    label: "Escalation clause",
    type: "escalation",
    token: "offer.escalation",
    example: "Starting at $475,000, $1,000 over any competing offer, up to $500,000",
    hint:
      "An escalation clause: the starting price, the step over a competing offer, and the cap.",
  },
  {
    key: "lease_back",
    label: "Seller lease-back",
    type: "lease_back",
    token: "offer.lease_back",
    example: "30 days at $2,000",
    hint:
      "The seller staying on after close: for how many days, and the rent (or 'free'). Agents " +
      "also say rent-back or post-possession.",
  },
  // Nate, 2026-11-03: "when explaining offer terms, the OS should
  // recognize terms such as the SCA, which is equivalent to the seller
  // compensation addendum or buyers agent compensation. Typically a
  // percentage. For the HOA addendum, terms such as who will pay the
  // transfer fee or the capital improvement fee are boxes that are checked
  // here." His "3.00% SCA ... buyer pays HOA transfer fee and capital
  // improvement fee" came back as "nothing in that reads as an offer term."
  {
    key: "buyer_agent_compensation",
    label: "Buyer's agent compensation (SCA)",
    type: "money_or_percent",
    token: "offer.buyer_agent_compensation",
    example: "3.00% or $9,000",
    hint:
      "What the seller pays the buyer's agent, written on the Seller Compensation Addendum. " +
      "Agents call it the SCA, seller compensation, buyer's agent compensation or buyer's agent " +
      "commission; '3% SCA' means 3.00%. Usually a percentage of the purchase price; " +
      "ALWAYS ambiguous unless the summary says % or $.",
  },
  {
    key: "hoa_transfer_fee_paid_by",
    label: "HOA transfer fee paid by",
    type: "enum",
    token: "offer.hoa_transfer_fee_paid_by",
    options: HOA_FEE_PAYERS,
    example: "Seller",
    hint: "Who pays the HOA transfer fee, ticked on the HOA addendum.",
  },
  {
    key: "hoa_capital_improvement_fee_paid_by",
    label: "HOA capital improvement fee paid by",
    type: "enum",
    token: "offer.hoa_capital_improvement_fee_paid_by",
    options: HOA_FEE_PAYERS,
    example: "Buyer",
    hint:
      "Who pays the HOA capital improvement (or capital contribution / reserve) fee, ticked on " +
      "the HOA addendum.",
  },
  {
    key: "offer_expires",
    label: "Offer expires",
    type: "datetime",
    token: "offer.offer_expires",
    example: "September 3, 2026 at 5:00 PM",
    hint:
      "When the offer comes off the table: a date and a time of day, Arizona time. 'Friday " +
      "5pm' or 'tomorrow at noon' is written as the real date it means (interpreted).",
  },
];

const BY_KEY = new Map(OFFER_SLOTS.map((s) => [s.key, s]));
const BY_TOKEN = new Map(OFFER_SLOTS.map((s) => [s.token, s]));

export function findOfferSlot(key: string): OfferSlot | null {
  return BY_KEY.get(key) ?? null;
}

export function findOfferSlotByToken(token: string): OfferSlot | null {
  return BY_TOKEN.get(token) ?? null;
}

export function isOfferSlotKey(v: unknown): v is string {
  return typeof v === "string" && BY_KEY.has(v);
}

/** Every `offer.*` merge token, for the merge-field catalog. */
export const OFFER_SLOT_TOKENS: readonly string[] = OFFER_SLOTS.map((s) => s.token);

// ─── Provenance ────────────────────────────────────────────────────
//
// How a stored value came to be. Three states, kept distinct because
// "the model said it and he agreed" and "the model said it and he
// fixed it" are different facts about the same number, and an audit
// row that blurs them answers the wrong question later.

export type OfferValueProvenance =
  /** Proposed by Stew, accepted unchanged. */
  | "ai_accepted"
  /** Proposed by Stew, changed by the advisor before accepting. */
  | "ai_edited"
  /** Typed by the advisor; Stew proposed nothing here. */
  | "typed";

export function isOfferValueProvenance(v: unknown): v is OfferValueProvenance {
  return v === "ai_accepted" || v === "ai_edited" || v === "typed";
}

// ─── How sure, and why that is not a number ────────────────────────
//
// The design's central claim: CONFIDENCE IS THE WRONG GATE,
// INTERPRETATION IS THE RIGHT ONE. A model repeating a number the
// advisor said out loud is safe even at middling confidence. A model
// that CONVERTED something is dangerous even at high confidence —
// "three fifty" formatted as $350,000 is confident, plausible, and
// possibly $3.50 a square foot.
//
// So there is no 0-1 score anywhere in this pipeline. There are three
// named states, and only one of them starts checked.

export type OfferReadingKind =
  /** The advisor stated this value in this form. No conversion. */
  | "heard"
  /** The model converted, expanded, or inferred to get here. */
  | "interpreted"
  /** More than one defensible reading of what was said. */
  | "ambiguous";

export function isOfferReadingKind(v: unknown): v is OfferReadingKind {
  return v === "heard" || v === "interpreted" || v === "ambiguous";
}

/**
 * Whether a proposal starts checked in the review panel.
 *
 * ONLY a value the advisor literally said, unconverted, starts checked.
 * Everything else is theirs to look at first. This function is the
 * whole gate, which is why it is one line with a name rather than an
 * inline `=== "heard"` in a component.
 */
export function startsChecked(reading: OfferReadingKind): boolean {
  return reading === "heard";
}
